import type { LayoutListSchemaType } from '@vedalib/editor/lib/brand'
import { SCHEMA } from '@vedalib/editor/lib/brand'
import React, { useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import type { IFieldConfig } from 'components/formKit'
import { FieldNumber, FieldRadio, FieldSegmented, FormField } from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { Typography } from 'components/uiKit/Typography'
import { IDENTITY } from 'constants/commonConstans'
import { FLEX_DIRECTION_OPTIONS } from 'services/Branding/constants'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { t } from 'services/Translation'

import type { LayoutListFormBrandType } from './layoutListTypes'

const useDisabledColumn = ({ name, getter = IDENTITY }: IFieldConfig) => {
  const styles = useFormData<LayoutListSchemaType>(name, 2)
  const disabled = getter(styles?.settings?.flexDirection) === 'column'
  return useMemo(() => ({ disabled }), [disabled])
}

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.layoutList.style, {
  settings: {
    flexDirection: genBrandField({
      as: FieldSegmented<'row' | 'column'>,
      label: t('input.label.layout'),
      props: { options: FLEX_DIRECTION_OPTIONS },
    }),
    justifyContent: genBrandField({
      as: FieldRadio<'start' | 'normal' | 'center' | 'end'>,
      label: t('input.label.justifyContent'),
      props: {
        resettable: true,
        hideRadio: true,
        direction: 'horizontal',
        options: [
          { value: 'normal', label: <Icon name='alignmentAlignFull' size={KitSize.S} /> },
          { value: 'start', label: <Icon name='alignmentAlignLeft' size={KitSize.S} /> },
          { value: 'center', label: <Icon name='alignmentAlignMid' size={KitSize.S} /> },
          { value: 'end', label: <Icon name='alignmentAlignRight' size={KitSize.S} /> },
        ],
      },
    }),
  },
  indents: {
    columnGap: genBrandField({
      as: FieldNumber,
      label: t('elements.layoutList.schema.iconMargin'),
      props: { width: 64 },
      useConfigs: [useDisabledColumn],
    }),
    rowGap: genBrandField({
      as: FieldNumber,
      label: t('elements.layoutList.schema.sectionsMargin'),
      rules: [{ min: 0, message: ' ' }],
      props: { width: 64 },
    }),
  },
})

const LayoutListForm: LayoutListFormBrandType = ({ device, subConfigs }) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  return (
    <Padding bottomSpace gap sides>
      <FormField config={schema.settings.flexDirection} />
      <FormField config={schema.settings.justifyContent} />

      <Typography weight='medium'>{t('elements.layoutList.tags.indents')}</Typography>
      <FormField config={schema.indents.columnGap} />
      <FormField config={schema.indents.rowGap} />
    </Padding>
  )
}

export default React.memo(LayoutListForm) as typeof LayoutListForm
